
// Vue
import { Component, Vue } from "vue-property-decorator";
import { namespace, Getter, Action } from "vuex-class";

// Utilities
import { maxLength, email, requiredIf, required } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import { get, isEqual } from "lodash";

// Types
import { PasswordModel } from "@/store/my-account/types.ts";
import { FeaturesList } from "@/store/types";
import { UserDetails, RecoveryQuestion, UserQuestionsUpdate, PasswordChangeResult } from "@/store/user-management/types";

// Components
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import UserSettings from "./UserSettings.vue";
import vselect3 from "vselect3";

// Services
import api from "@/services/api.service";

// vuex namespaces
const UserManagement = namespace("userManagement");

@Component({
	mixins: [validationMixin],
	validations: {
		userInformation: {
			email: {
				maxLength: maxLength(150)
			},
			address: {
				maxLength: maxLength(200)
			},
			telephone: {
				maxLength: maxLength(50)
			},
			mobile: {
				maxLength: maxLength(50)
			},
			verifyChange: function verifyChange() {
				return !isEqual(this.userInformation, this.userInformationTemplate);
			}
		},
		userAnswer1: { required },
		userAnswer2: { required },
		userAnswer3: { required },
		confirmationPassword: { required }
	},

	components: {
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		vselect3,
		"user-settings": UserSettings,
	}
})
export default class AccountDetails extends Vue {
	@Getter("getUserId") currentUserId: number;

	@Getter("getFeaturesList") featuresList: FeaturesList;

	@Getter getFeature: (featureName: string[]) => boolean;

	@Action getTimeZones: any;
	@UserManagement.Action updateUser: any;
	@UserManagement.Action getUserById: any;
	@UserManagement.Action private getRecoveryQuestions: () => Promise<RecoveryQuestion[]>;
	@UserManagement.Action private getUserQuestions: (username: string) => Promise<RecoveryQuestion[]>;
	@UserManagement.Action private updateUserQuestions: (updatedUserQuestions: UserQuestionsUpdate) => Promise<number>;

	private userQuestions: RecoveryQuestion[] = [];

	private recoveryQuestionsList: RecoveryQuestion[] = [];

	private selectedQuestion1: RecoveryQuestion = { userQuestionId: null, title: "" };

	private userAnswer1: string = null;

	private selectedQuestion2: RecoveryQuestion = { userQuestionId: null, title: "" };

	private userAnswer2: string = null;

	private selectedQuestion3: RecoveryQuestion = { userQuestionId: null, title: "" };

	private userAnswer3: string = null;

	private confirmationPassword: string = null;

	private TimeZones = [];

	private BrowserTimeZone: string = null;

	private showPasswordForm: boolean = false;

	private showQuestionsForm: boolean = false;

	private errorMessage: string = null;

	private complexityChecks: { [key: string]: number } = null;

	private passwordRequest: PasswordModel = {
		OldPassword: "",
		NewPassword: "",
		ConfirmPassword: ""
	};

	private result: PasswordChangeResult = {
		validationMessage: null,
		complexityChecks: null,
		successful: false
	};

	private questionsUpdate: UserQuestionsUpdate = {
		question1Id: null,
		answer1: "",
		question2Id: null,
		answer2: "",
		question3Id: null,
		answer3: "",
		confirmationPassword: ""
	};

	public get isEditPersonalEnabled() {
		if (get(this.featuresList, ["AccountDetails", "EditPersonalInformation"]) == null) {
			return false;
		} else {
			return get(this.featuresList, ["AccountDetails", "EditPersonalInformation"]);
		}
	}

	private async changePassword() {
		this.errorMessage = null;
		this.result = await api.changePassword(this.passwordRequest);

		if (this.result && this.result.successful) {
			this.showPasswordForm = false;
		} else {
			this.errorMessage = this.result.validationMessage;
			this.complexityChecks = this.result.complexityChecks;
		}
	}

	private saveIsEnabled() {
		return (
			this.passwordRequest.OldPassword != null &&
			this.passwordRequest.OldPassword != "" &&
			this.passwordRequest.NewPassword != null &&
			this.passwordRequest.NewPassword != "" &&
			this.passwordRequest.ConfirmPassword != null &&
			this.passwordRequest.ConfirmPassword != ""
		);
	}
	private closePasswordForm() {
		this.showPasswordForm = !this.showPasswordForm;

		this.passwordRequest = {
			OldPassword: "",
			NewPassword: "",
			ConfirmPassword: ""
		};

		this.result = {
			validationMessage: null,
			complexityChecks: null,
			successful: false
		};
	}

	private closeQuestionsForm() {
		this.showQuestionsForm = !this.showQuestionsForm;

		if (this.userQuestions && this.userQuestions.length == 3) {
			this.selectedQuestion1 = this.userQuestions[0];
			this.selectedQuestion2 = this.userQuestions[1];
			this.selectedQuestion3 = this.userQuestions[2];
		}
		this.userAnswer1 = null;
		this.userAnswer2 = null;
		this.userAnswer3 = null;

		this.confirmationPassword = null;
	}

	private userTimeZone = { timeZoneId: 0, title: "string" };
	private userEmail: string = "";
	private userTel: string = "string";
	private userMobile: string = "string";
	private userAddress: string = "string";

	private userInformationTemplate: any = null;

	private userInformation: UserDetails = {
		userID: 0,
		username: "",
		fullName: "",
		email: "",
		address: null,
		telephone: null,
		mobile: null,
		groupID: 0,
		isSiteStaff: false,
		mobileAppEnabled: false,
		groupName: null,
		disabledAt: null,
		lockedOutUntil: null,
		lastLoginAt: null,
		password: "",
		permissions: null,
		permissionsGroup: null,
		userGroupMembershipTitles: [null],
		readOnly: false,
		timeZoneId: 0
	};

	private async mounted() {
		this.userInformation = await this.getUserById(this.currentUserId);
		this.userInformationTemplate = Object.assign({}, this.userInformation);

		this.BrowserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		this.retrieveQuestions();
	}

	private async retrieveQuestions() {
		this.recoveryQuestionsList = await this.getRecoveryQuestions();
		try {
			// Existing Users may not have their questions set however we still want them to be able set their questions
			this.userQuestions = await this.getUserQuestions(this.userInformation.username);
		} catch (error) {
			console.log("Failed to retrieve existing user recovery questions: " + error);
		}
		if (this.userQuestions && this.userQuestions.length == 3) {
			this.selectedQuestion1 = this.userQuestions[0];
			this.selectedQuestion2 = this.userQuestions[1];
			this.selectedQuestion3 = this.userQuestions[2];
		} else {
			// If user doesnt have questions set default to the first 3 questions in the list of recovery questins
			this.selectedQuestion1 = this.recoveryQuestionsList[0];
			this.selectedQuestion2 = this.recoveryQuestionsList[1];
			this.selectedQuestion3 = this.recoveryQuestionsList[2];
		}
	}

	private async saveUserDetails() {
		await this.updateUser(this.userInformation);

		this.userInformationTemplate = Object.assign({}, this.userInformation);
	}

	private resetUser() {
		this.userInformation = Object.assign({}, this.userInformationTemplate);
	}

	private async saveUserQuestions() {
		(this.questionsUpdate.question1Id = this.selectedQuestion1.userQuestionId),
			(this.questionsUpdate.answer1 = this.userAnswer1),
			(this.questionsUpdate.question2Id = this.selectedQuestion2.userQuestionId),
			(this.questionsUpdate.answer2 = this.userAnswer2),
			(this.questionsUpdate.question3Id = this.selectedQuestion3.userQuestionId),
			(this.questionsUpdate.answer3 = this.userAnswer3),
			(this.questionsUpdate.confirmationPassword = this.confirmationPassword);
		try {
			let updatedUserId = await this.updateUserQuestions(this.questionsUpdate);
			// Update our local user questions after saving them in case he user returns without reopening account details modal
			if (updatedUserId) {
				await this.retrieveQuestions();
			}
		} catch (error) {
			console.log("Failed to save user questions: " + error);
		}

		this.closeQuestionsForm();
	}
}
